import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import AdminSidebar from '@/pages/admin/dashboard/components/AdminSidebar';
import StoryForm from './components/StoryForm';

interface Story {
  id: string;
  slug: string;
  title_en: string;
  title_ps: string;
  title_da: string;
  excerpt_en: string;
  excerpt_ps: string;
  excerpt_da: string;
  content_en: string;
  content_ps: string;
  content_da: string;
  author_en: string;
  author_ps: string;
  author_da: string;
  author_role_en: string;
  author_role_ps: string;
  author_role_da: string;
  image: string;
  category: string;
  read_time_en: string;
  read_time_ps: string;
  read_time_da: string;
  published: boolean;
  created_at: string;
  updated_at: string;
}

const CATEGORIES = ['Field Stories', 'Impact Reports', 'Volunteer Voices', 'News & Updates'];
const CATEGORY_COLORS: Record<string, string> = {
  'Field Stories': 'bg-emerald-100 text-emerald-700',
  'Impact Reports': 'bg-amber-100 text-amber-700',
  'Volunteer Voices': 'bg-rose-100 text-rose-700',
  'News & Updates': 'bg-sky-100 text-sky-700',
};

export default function AdminStoriesPage() {
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStory, setEditingStory] = useState<Story | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('All');

  const fetchStories = useCallback(async () => {
    setLoading(true);
    let query = supabase.from('stories').select('*').order('created_at', { ascending: false });
    if (filterCat !== 'All') query = query.eq('category', filterCat);
    const { data } = await query;
    setStories((data as Story[]) ?? []);
    setLoading(false);
  }, [filterCat]);

  useEffect(() => { fetchStories(); }, [fetchStories]);

  const handleTogglePublish = async (id: string, current: boolean) => {
    await supabase.from('stories').update({ published: !current, updated_at: new Date().toISOString() }).eq('id', id);
    fetchStories();
  };

  const handleDelete = async (id: string) => {
    await supabase.from('stories').delete().eq('id', id);
    setDeleteConfirm(null);
    fetchStories();
  };

  const handleEdit = (story: Story) => {
    setEditingStory(story);
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingStory(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingStory(null);
    fetchStories();
  };

  const filteredStories = stories.filter(s =>
    s.title_en.toLowerCase().includes(search.toLowerCase()) ||
    s.slug.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const hasTranslation = (s: Story, lang: 'ps' | 'da') => {
    return lang === 'ps' ? !!s.title_ps : !!s.title_da;
  };

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      <AdminSidebar />
      <main className="flex-1 p-8 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1B2838]">Stories</h1>
            <p className="text-gray-500 text-sm mt-0.5">Manage blog posts and stories — Pashto & Dari translations managed separately in CMS</p>
          </div>
          <button
            onClick={handleNew}
            className="px-5 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] transition-colors whitespace-nowrap flex items-center gap-2"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></div>
            New Story
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-5">
          <div className="relative flex-1 max-w-sm">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-search-line"></i>
            </div>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search stories..."
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:border-[#4ECDC4]"
            />
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-1 flex gap-1">
            <button
              onClick={() => setFilterCat('All')}
              className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${filterCat === 'All' ? 'bg-[#1B2838] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              All
            </button>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${filterCat === cat ? 'bg-[#1B2838] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="py-20 text-center">
              <div className="w-7 h-7 border-2 border-[#4ECDC4] border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-sm text-gray-400 mt-3">Loading stories...</p>
            </div>
          ) : filteredStories.length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3"><i className="ri-book-open-line text-3xl text-gray-300"></i></div>
              <p className="text-gray-500 text-sm mb-4">No stories found</p>
              <button onClick={handleNew} className="text-[#4ECDC4] font-semibold text-sm cursor-pointer hover:text-[#3dbdb5]">
                + Create your first story
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Story</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">PS</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">DA</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredStories.map(story => (
                  <tr key={story.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                          {story.image ? (
                            <img src={story.image} alt="" className="w-full h-full object-cover object-top" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-300"><i className="ri-image-line"></i></div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-[#1B2838] truncate max-w-xs">{story.title_en}</div>
                          <div className="text-xs text-gray-400 mt-0.5">/{story.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${CATEGORY_COLORS[story.category] || 'bg-gray-100 text-gray-600'}`}>
                        {story.category}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      {hasTranslation(story, 'ps') ? (
                        <span className="text-xs text-[#4ECDC4] font-medium">✓</span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-center">
                      {hasTranslation(story, 'da') ? (
                        <span className="text-xs text-[#4ECDC4] font-medium">✓</span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-xs text-gray-500 whitespace-nowrap">{formatDate(story.created_at)}</td>
                    <td className="px-5 py-4 text-center">
                      <button
                        onClick={() => handleTogglePublish(story.id, story.published)}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full cursor-pointer transition-colors whitespace-nowrap ${story.published ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                      >
                        {story.published ? 'Published' : 'Draft'}
                      </button>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(story)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-[#1B2838] hover:bg-gray-100 cursor-pointer transition-colors"
                          title="Edit"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        {deleteConfirm === story.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(story.id)}
                              className="px-3 py-1.5 bg-red-500 text-white text-xs font-semibold rounded-lg cursor-pointer hover:bg-red-600 transition-colors whitespace-nowrap"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-semibold rounded-lg cursor-pointer hover:bg-gray-200 transition-colors whitespace-nowrap"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(story.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Story count */}
        <div className="mt-3 text-xs text-gray-400">
          {stories.length} story{stories.length !== 1 ? 'ies' : ''} total — {stories.filter(s => s.published).length} published, {stories.filter(s => !s.published).length} drafts
        </div>
      </main>

      {/* Story Form Modal */}
      {showForm && (
        <StoryForm story={editingStory} onClose={handleFormClose} />
      )}
    </div>
  );
}